"use client";
import { ProcessedCourse } from "@/common/course/types";
import CourseEnroll from "../CourseEnroll";
import { sortCourseRuns } from "@/components/CourseContent/ContentRefresh/CourseRunSelector/utils/sortCourseRuns";
import { cn } from "@/lib/utils";
import type { DetailPricingData } from "@/lib/checkout-geo-pricing";

type Props = {
  course: ProcessedCourse;
  name?: string;
  location?: string;
  auditTextClassName?: string;
  showEndDate?: boolean;
  auditLinkLocation?: string;
  variant?: "box" | "inline" | "inlineHeader";
  variantStyle?: "light" | "dark";
  isInCollapsedBody?: boolean;
  className?: string;
  straightToCheckout?: boolean;
  pricingData?: DetailPricingData | null;
};

export default function CourseRunSelector({
  course,
  name = "",
  location = "",
  auditTextClassName,
  showEndDate = true,
  auditLinkLocation = "",
  variant = "box",
  variantStyle = "light",
  isInCollapsedBody = false,
  className = "",
  straightToCheckout,
  pricingData,
}: Readonly<Props>) {
  const sortedCourseRuns = sortCourseRuns(course.activeCourseRuns);

  // Use the active course run determined by getProcessedCourseFromCourse
  // instead of just taking the first sorted course run
  const mainCourseRun = course.activeCourseRun || sortedCourseRuns?.at(0);

  const multipleRunsAvailable = (sortedCourseRuns || []).length > 1;

  let seeMoreDatesHandler = false;

  if (multipleRunsAvailable && location !== "side-rail") {
    seeMoreDatesHandler = true;
  }

  const bgClassName =
    variantStyle === "dark" && variant === "box"
      ? cn(
          `border-none bg-primary-500 text-white md:bg-putty-100 md:text-primary-500 md:border-putty-400 p-0 md:p-4 `,
          className,
        )
      : cn(className);

  return (
    <div id="enroll">
      {mainCourseRun ? (
        <CourseEnroll
          key={mainCourseRun.id}
          courseRun={mainCourseRun}
          course={course}
          data-testid={`course-run-selector-run-${mainCourseRun.id}`}
          seeMoreDatesHandler={seeMoreDatesHandler}
          variant={variant}
          name={name}
          location={location}
          auditTextClassName={auditTextClassName}
          showEndDate={showEndDate}
          auditLinkLocation={auditLinkLocation}
          className={bgClassName}
          isInCollapsedBody={isInCollapsedBody}
          straightToCheckout={straightToCheckout}
          pricingData={pricingData}
        />
      ) : null}
    </div>
  );
}
